import React, { createContext, useContext, useState, useEffect } from 'react';

const QuoteContext = createContext();

// Session storage key for quote data
const QUOTE_STORAGE_KEY = 'h-autorepair-quote-data';

// Helper functions for session storage
const saveToSessionStorage = (data) => {
  try {
    sessionStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
  }
};

const loadFromSessionStorage = () => {
  try {
    const stored = sessionStorage.getItem(QUOTE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

const clearSessionStorage = () => {
  try {
    sessionStorage.removeItem(QUOTE_STORAGE_KEY);
  } catch (error) {
  }
};

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
};

export const QuoteProvider = ({ children }) => {
  // Initialize with default data or load from session storage
  const getInitialQuoteData = () => {
    const storedData = loadFromSessionStorage();
    if (storedData) {
      // Migrate old data to include completedSteps if missing
      if (!storedData.completedSteps) {
        storedData.completedSteps = {
          vehicle: false,
          services: false,
          contact: false,
        };
      }
      return storedData;
    }
    
    return {
      // Basic quote info
      id: null,
      status: 'draft', // draft, submitted, pending_price, confirmed, completed, cancelled
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
      // Step completion flags (custom quote flow only)
      completedSteps: {
        vehicle: false,    // Step 1: Vehicle Information
        services: false,   // Step 2: Services
        contact: false,    // Step 3: Contact Information
      },
      
      // Step 1: Vehicle Information
      vehicle: {
        vin: "",
        year: "",
        make: "",
        model: "",
        trim: "",
        mileage: "",
        olderThan1984: false,
      },
      
      // Step 2: Services (from CartContext)
      services: [], // This will be synced with cartItems
      
      // Zip entered at quote start (approximate area for pricing) - distinct from location.zipCode (full address after approval)
      serviceAreaZip: "",
      
      // Step 3: Location Information
      location: {
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      
      // Step 4: Schedule Information
      schedule: {
        date: "",
        time: "",
        preferredTimeSlot: "", // morning, afternoon, evening
      },
      
      // Step 5: Contact Information
      contact: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        hearAboutUs: "",
        emailOffers: true,
        textOffers: true,
      },
      
      // Additional quote details
      totalAmount: 0,
      notes: "",
      specialInstructions: "",
      
      // Database fields
      userId: null,
      employeeId: null,
      assignedDate: null,
      completedDate: null,
    };
  };

  const [quoteData, setQuoteData] = useState(getInitialQuoteData);

  // Save to session storage whenever quoteData changes
  useEffect(() => {
    saveToSessionStorage(quoteData);
  }, [quoteData]);

  // Update specific sections of quote data
  const updateVehicleData = (data) => {
    setQuoteData(prev => ({
      ...prev,
      vehicle: { ...prev.vehicle, ...data },
      updatedAt: new Date().toISOString()
    }));
  };

  const updateContactData = (data) => {
    setQuoteData(prev => ({
      ...prev,
      contact: { ...prev.contact, ...data },
      updatedAt: new Date().toISOString()
    }));
  };

  const updateLocationData = (data) => {
    setQuoteData(prev => {
      const merged = { ...prev.location, ...data };
      // No longer collected on the public location step — keep context aligned with the form
      const { locationType: _omitType, keyNotes: _omitKeys, ...location } = merged;
      return {
        ...prev,
        location,
        updatedAt: new Date().toISOString()
      };
    });
  };

  const updateServiceAreaZip = (zip) => {
    setQuoteData(prev => ({
      ...prev,
      serviceAreaZip: zip || "",
      updatedAt: new Date().toISOString()
    }));
  };

  const updateScheduleData = (data) => {
    setQuoteData(prev => ({
      ...prev,
      schedule: { ...prev.schedule, ...data },
      updatedAt: new Date().toISOString()
    }));
  };

  const updateServicesData = (services) => {
    setQuoteData(prev => ({
      ...prev,
      services: services,
      updatedAt: new Date().toISOString()
    }));
  };

  const updateQuoteStatus = (status) => {
    setQuoteData(prev => ({
      ...prev,
      status: status,
      updatedAt: new Date().toISOString()
    }));
  };

  const updateTotalAmount = (amount) => {
    setQuoteData(prev => ({
      ...prev,
      totalAmount: amount,
      updatedAt: new Date().toISOString()
    }));
  };

  const updateNotes = (notes) => {
    setQuoteData(prev => ({
      ...prev,
      notes: notes,
      updatedAt: new Date().toISOString()
    }));
  };

  // Get complete quote for database save
  const getCompleteQuote = () => {
    return {
      ...quoteData,
      // Ensure all required fields are present
      vehicle: quoteData.vehicle,
      services: quoteData.services,
      location: quoteData.location,
      schedule: quoteData.schedule,
      contact: quoteData.contact,
      totalAmount: quoteData.totalAmount,
      status: quoteData.status,
      createdAt: quoteData.createdAt,
      updatedAt: new Date().toISOString()
    };
  };

  // Clear all quote data (for new quote)
  const clearQuoteData = () => {
    const newQuoteData = {
      id: null,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      completedSteps: {
        vehicle: false,
        services: false,
        contact: false,
      },
      vehicle: {
        vin: "",
        year: "",
        make: "",
        model: "",
        trim: "",
        mileage: "",
        olderThan1984: false,
      },
      services: [],
      serviceAreaZip: "",
        location: {
          address: "",
          city: "",
          state: "",
          zipCode: "",
        },
      schedule: {
        date: "",
        time: "",
        preferredTimeSlot: "",
      },
      contact: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        hearAboutUs: "",
        emailOffers: true,
        textOffers: true,
      },
      totalAmount: 0,
      notes: "",
      specialInstructions: "",
      userId: null,
      employeeId: null,
      assignedDate: null,
      completedDate: null,
    };
    setQuoteData(newQuoteData);
    // Clear from session storage as well
    clearSessionStorage();
  };

  // Set quote ID (when saved to database)
  const setQuoteId = (id) => {
    setQuoteData(prev => ({
      ...prev,
      id: id,
      updatedAt: new Date().toISOString()
    }));
  };

  // Force reload from session storage (useful for debugging or manual refresh)
  const reloadFromStorage = () => {
    const storedData = loadFromSessionStorage();
    if (storedData) {
      setQuoteData(storedData);
    }
  };

  // Mark a step as completed
  const markStepCompleted = (stepName) => {
    setQuoteData(prev => ({
      ...prev,
      completedSteps: {
        ...prev.completedSteps,
        [stepName]: true
      },
      updatedAt: new Date().toISOString()
    }));
  };

  // Mark a step as incomplete (when flow changes)
  const markStepIncomplete = (stepName) => {
    setQuoteData(prev => ({
      ...prev,
      completedSteps: {
        ...prev.completedSteps,
        [stepName]: false
      },
      updatedAt: new Date().toISOString()
    }));
  };

  // Check if a step can be accessed (all previous steps completed)
  const canAccessStep = (stepName) => {
    // Check if we're in approved quote flow (has quote ID)
    const isApprovedQuoteFlow = quoteData?.id;
    
    if (isApprovedQuoteFlow) {
      // For approved quote flow: only location, schedule, and book are accessible
      // Location is always accessible (first step)
      // Schedule is accessible only if location is completed
      // Book is accessible only if both location and schedule are completed
      if (stepName === 'location') {
        return true; // Location is always accessible in approved flow
      } else if (stepName === 'schedule') {
        // Schedule is accessible if location is completed
        const completedSteps = quoteData?.completedSteps || {};
        return completedSteps.location === true;
      } else if (stepName === 'book') {
        // Book is accessible if both location and schedule are completed
        const completedSteps = quoteData?.completedSteps || {};
        return completedSteps.location === true && completedSteps.schedule === true;
      } else {
        // All other steps (vehicle, services, contact) are not accessible in approved flow
        return false;
      }
    } else {
      // Custom quote flow: only vehicle, services, contact are accessible
      const stepOrder = ['vehicle', 'services', 'contact'];
      const stepIndex = stepOrder.indexOf(stepName);
      
      if (stepIndex === -1) return false; // Step not in custom flow
      if (stepIndex === 0) return true; // First step is always accessible
      
      // Ensure completedSteps exists and has default values
      const completedSteps = quoteData?.completedSteps || {
        vehicle: false,
        services: false,
        contact: false,
      };
      
      // Check if all previous steps are completed
      for (let i = 0; i < stepIndex; i++) {
        if (!completedSteps[stepOrder[i]]) {
          return false;
        }
      }
      return true;
    }
  };

  // Clear data from a specific step and mark it as incomplete
  const clearStepData = (stepName) => {
    const stepDataMap = {
      vehicle: {
        vehicle: {
          vin: "",
          year: "",
          make: "",
          model: "",
          trim: "",
          mileage: "",
          olderThan1984: false,
        }
      },
      services: {
        services: []
      },
      location: {
        location: {
          address: "",
          city: "",
          state: "",
          zipCode: "",
        }
      },
      schedule: {
        schedule: {
          date: "",
          time: "",
          preferredTimeSlot: "",
        }
      },
      contact: {
        contact: {
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          hearAboutUs: "",
          emailOffers: true,
          textOffers: true,
        }
      }
    };

    setQuoteData(prev => ({
      ...prev,
      ...stepDataMap[stepName],
      completedSteps: {
        ...prev.completedSteps,
        [stepName]: false
      },
      updatedAt: new Date().toISOString()
    }));
  };

  // Clear all steps after a specific step (when flow changes)
  const clearStepsAfter = (stepName) => {
    // Only clear steps in custom quote flow
    const stepOrder = ['vehicle', 'services', 'contact'];
    const stepIndex = stepOrder.indexOf(stepName);
    
    if (stepIndex === -1) return;
    
    // Clear data and mark as incomplete for all steps after the given step
    const stepsToClear = stepOrder.slice(stepIndex + 1);
    
    setQuoteData(prev => {
      const newData = { ...prev };
      
      stepsToClear.forEach(step => {
        // Clear step data
        if (step === 'services') {
          newData.services = [];
        } else if (step === 'location') {
          newData.location = {
            address: "",
            city: "",
            state: "",
            zipCode: "",
          };
        } else if (step === 'schedule') {
          newData.schedule = {
            date: "",
            time: "",
            preferredTimeSlot: "",
          };
        } else if (step === 'contact') {
          newData.contact = {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            hearAboutUs: "",
            emailOffers: true,
            textOffers: true,
          };
        }
        
        // Mark step as incomplete
        newData.completedSteps[step] = false;
      });
      
      newData.updatedAt = new Date().toISOString();
      return newData;
    });
  };

  const value = {
    quoteData,
    updateVehicleData,
    updateContactData,
    updateLocationData,
    updateServiceAreaZip,
    updateScheduleData,
    updateServicesData,
    updateQuoteStatus,
    updateTotalAmount,
    updateNotes,
    getCompleteQuote,
    clearQuoteData,
    setQuoteId,
    reloadFromStorage,
    markStepCompleted,
    markStepIncomplete,
    canAccessStep,
    clearStepData,
    clearStepsAfter
  };

  return (
    <QuoteContext.Provider value={value}>
      {children}
    </QuoteContext.Provider>
  );
}; 